import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Plus, X, Check, Minus, Shield, Lock, Briefcase, 
  Building2, Truck, Users, Star, Package, ChevronDown, Search, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogoFull } from '@/components/Logo';
import { api } from '@/services/api';

const ICON_MAP = {
  shield: Shield, lock: Lock, briefcase: Briefcase, building: Building2,
  'trending-down': Shield, users: Users, 'user-check': Star,
  star: Star, truck: Truck, package: Package, 'users-round': Users, container: Package
};

const CATEGORY_LABELS = {
  liability: 'Liability',
  cyber: 'Cyber & Tech',
  property: 'Property',
  auto: 'Auto & Fleet',
  employee: 'Employee',
  benefits: 'Employee Benefits',
  executive: 'Executive & Management',
  crime: 'Crime & Surety',
  specialty: 'Specialty',
  logistics: 'Logistics',
};

const CATEGORY_COLORS = {
  liability: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  cyber: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
  property: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  auto: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  employee: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  benefits: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
  executive: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  crime: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  specialty: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  logistics: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
};

const CoverageCard = ({ block, isSelected, onToggle }) => {
  const IconComponent = ICON_MAP[block.icon] || Shield;
  const cat = CATEGORY_COLORS[block.category] || CATEGORY_COLORS.liability;

  return (
    <motion.button
      layout
      whileHover={{ scale: 1.01 }}
      onClick={() => onToggle(block)}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
        isSelected 
          ? 'border-[#00AEEF] bg-[#00AEEF]/5 shadow-md' 
          : 'border-[#DCE4ED] bg-white hover:border-[#00AEEF]/40'
      }`}
      data-testid={`compare-card-${block.coverage_type}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#1A3A64]' : 'bg-[#1A3A64]/10'}`}>
          <IconComponent size={18} className={isSelected ? 'text-white' : 'text-[#1A3A64]'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-[#0D1B2A] text-sm truncate">{block.name}</h4>
            {isSelected && <Check size={16} className="text-[#00AEEF] flex-shrink-0" />}
          </div>
          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${cat.bg} ${cat.text} mb-1.5`}>
            {CATEGORY_LABELS[block.category] || block.category}
          </span>
          <p className="text-xs text-[#3D5A80] line-clamp-2">{block.description}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-semibold text-[#1A3A64]">${block.base_premium}</p>
          <p className="text-[10px] text-[#8DA0B9]">/mo</p>
        </div>
      </div>
    </motion.button>
  );
};

const ComparisonTable = ({ selected, onRemove }) => {
  if (selected.length === 0) return null;

  const fields = [
    { key: 'description', label: 'What it covers' },
    { key: 'coverage_limit', label: 'Coverage Limit', format: v => v > 0 ? `$${v.toLocaleString()}` : 'Varies by plan' },
    { key: 'deductible', label: 'Deductible', format: v => v > 0 ? `$${v.toLocaleString()}` : '$0' },
    { key: 'base_premium', label: 'Monthly Premium', format: v => `$${v}/mo` },
    { key: 'annual', label: 'Annual (10% off)', format: (_, b) => `$${(b.base_premium * 12 * 0.9).toFixed(0)}/yr` },
    { key: 'included_perils', label: 'Covered Perils', format: v => v || [] },
    { key: 'exclusions', label: 'Exclusions', format: v => v || [] },
    { key: 'risk_factors', label: 'Pricing Factors', format: v => v || [] },
  ];

  return (
    <div className="overflow-x-auto" data-testid="comparison-table">
      <table className="w-full min-w-[600px]">
        <thead>
          <tr>
            <th className="p-3 text-left text-xs text-[#8DA0B9] uppercase tracking-wider font-medium w-[160px] sticky left-0 bg-[#F0F4F8] z-10">
              Feature
            </th>
            {selected.map(block => {
              const cat = CATEGORY_COLORS[block.category] || CATEGORY_COLORS.liability;
              return (
                <th key={block.coverage_type} className="p-3 min-w-[220px]">
                  <div className="bg-white rounded-xl border border-[#DCE4ED] p-3 relative">
                    <button
                      onClick={() => onRemove(block)}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-[#E74C3C] text-white rounded-full flex items-center justify-center"
                      data-testid={`remove-compare-${block.coverage_type}`}
                    >
                      <X size={12} />
                    </button>
                    <p className="font-semibold text-[#0D1B2A] text-sm">{block.name}</p>
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium mt-1 ${cat.bg} ${cat.text}`}>
                      {CATEGORY_LABELS[block.category]}
                    </span>
                    <p className="text-lg font-bold text-[#1A3A64] mt-2">${block.base_premium}<span className="text-xs font-normal text-[#8DA0B9]">/mo</span></p>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {fields.map((field, fi) => (
            <tr key={field.key} className={fi % 2 === 0 ? 'bg-white' : 'bg-[#F0F4F8]/50'}>
              <td className="p-3 text-xs font-medium text-[#3D5A80] sticky left-0 bg-inherit z-10 border-r border-[#DCE4ED]">
                {field.label}
              </td>
              {selected.map(block => {
                const value = block[field.key];
                const formatted = field.format ? field.format(value, block) : value;

                return (
                  <td key={block.coverage_type} className="p-3 text-sm align-top">
                    {Array.isArray(formatted) ? (
                      <ul className="space-y-1">
                        {formatted.map((item, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-xs text-[#3D5A80]">
                            {field.key === 'exclusions' ? (
                              <Minus size={10} className="text-[#E74C3C] mt-0.5 flex-shrink-0" />
                            ) : field.key === 'included_perils' ? (
                              <Check size={10} className="text-[#00C4AD] mt-0.5 flex-shrink-0" />
                            ) : (
                              <span className="w-1 h-1 rounded-full bg-[#8DA0B9] mt-1.5 flex-shrink-0" />
                            )}
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : field.key === 'description' ? (
                      <p className="text-xs text-[#3D5A80] leading-relaxed">{formatted}</p>
                    ) : (
                      <p className={`font-medium ${field.key === 'base_premium' || field.key === 'annual' ? 'text-[#1A3A64]' : 'text-[#0D1B2A]'}`}>
                        {formatted}
                      </p>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-[#DCE4ED]">
            <td className="p-3 text-xs font-bold text-[#0D1B2A] sticky left-0 bg-white z-10">
              Combined Total
            </td>
            <td colSpan={selected.length} className="p-3">
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-xs text-[#8DA0B9]">Monthly</p>
                  <p className="text-xl font-bold text-[#1A3A64]">
                    ${selected.reduce((s, b) => s + b.base_premium, 0).toLocaleString()}<span className="text-sm font-normal">/mo</span>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#8DA0B9]">Annual (save 10%)</p>
                  <p className="text-xl font-bold text-[#00C4AD]">
                    ${(selected.reduce((s, b) => s + b.base_premium, 0) * 12 * 0.9).toFixed(0)}<span className="text-sm font-normal">/yr</span>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#8DA0B9]">Total Coverage</p>
                  <p className="text-xl font-bold text-[#0D1B2A]">
                    ${selected.reduce((s, b) => s + (b.coverage_limit || 0), 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const PlanComparator = () => {
  const [blocks, setBlocks] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    api.getCoverageBlocks()
      .then(setBlocks)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const cats = [...new Set(blocks.map(b => b.category))];
    return cats.sort();
  }, [blocks]);

  const filtered = useMemo(() => {
    return blocks.filter(b => {
      const matchSearch = !search || 
        b.name.toLowerCase().includes(search.toLowerCase()) ||
        b.description.toLowerCase().includes(search.toLowerCase()) ||
        b.coverage_type.toLowerCase().includes(search.toLowerCase());
      const matchCategory = !categoryFilter || b.category === categoryFilter;
      return matchSearch && matchCategory;
    });
  }, [blocks, search, categoryFilter]);

  const toggleBlock = (block) => {
    setSelected(prev => {
      const exists = prev.find(b => b.coverage_type === block.coverage_type);
      if (exists) return prev.filter(b => b.coverage_type !== block.coverage_type);
      if (prev.length >= 4) return prev;
      return [...prev, block];
    });
  };

  const removeBlock = (block) => {
    setSelected(prev => prev.filter(b => b.coverage_type !== block.coverage_type));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1A3A64] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="plan-comparator-page">
      {/* Header */}
      <header className="bg-white border-b border-[#DCE4ED] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <Link to="/" className="flex items-center gap-2 text-[#8DA0B9] hover:text-[#0D1B2A] transition-colors">
              <ArrowLeft size={18} /><span className="text-sm">Home</span>
            </Link>
            <LogoFull size="small" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Plan Comparator
              </h1>
              <p className="text-sm text-[#8DA0B9]">
                Select up to 4 coverages to compare side by side &bull; {blocks.length} products available
              </p>
            </div>
            {selected.length > 0 && (
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ background: 'linear-gradient(90deg, rgba(0,174,239,0.1), rgba(0,196,173,0.1))' }}>
                  <span className="text-[#1A3A64] font-bold">{selected.length}</span>
                  <span className="text-[#8DA0B9]"> / 4 selected</span>
                </span>
                <Button variant="outline" size="sm" onClick={() => setSelected([])} className="border-[#DCE4ED]" data-testid="clear-comparison-btn">
                  Clear All
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Comparison Table */}
        <AnimatePresence>
          {selected.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8 bg-white rounded-2xl border border-[#DCE4ED] overflow-hidden shadow-sm"
            >
              <div className="p-4 border-b border-[#DCE4ED] bg-gradient-to-r from-[#1A3A64]/5 to-[#00AEEF]/5">
                <h2 className="font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Comparison
                </h2>
              </div>
              <div className="p-4">
                <ComparisonTable selected={selected} onRemove={removeBlock} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8DA0B9]" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search coverages..."
              className="pl-9 border-[#DCE4ED] bg-white"
              data-testid="comparator-search"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setCategoryFilter('')}
              className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium border transition-all flex items-center gap-1.5 ${
                !categoryFilter ? 'bg-[#1A3A64] text-white border-[#1A3A64]' : 'bg-white text-[#3D5A80] border-[#DCE4ED]'
              }`}
              data-testid="filter-all"
            >
              <Filter size={12} />All ({blocks.length})
            </button>
            {categories.map(cat => {
              const cc = CATEGORY_COLORS[cat] || CATEGORY_COLORS.liability;
              const count = blocks.filter(b => b.category === cat).length;
              return (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(categoryFilter === cat ? '' : cat)}
                  className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                    categoryFilter === cat 
                      ? `${cc.bg} ${cc.text} ${cc.border}` 
                      : 'bg-white text-[#3D5A80] border-[#DCE4ED] hover:border-[#00AEEF]/40'
                  }`}
                  data-testid={`filter-${cat}`}
                >
                  {CATEGORY_LABELS[cat] || cat} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(block => (
            <CoverageCard
              key={block.coverage_type}
              block={block}
              isSelected={selected.some(s => s.coverage_type === block.coverage_type)}
              onToggle={toggleBlock}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <Search size={40} className="text-[#DCE4ED] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#0D1B2A] mb-2">No coverages found</h3>
            <p className="text-[#8DA0B9]">Try adjusting your search or filter.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanComparator;
